import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { fetchPaypalConfig, createPaypalOrder, capturePaypalOrder } from "@/lib/api";
import { toast } from "sonner";

/**
 * PayPalCheckout — renders PayPal buttons that create + capture an order
 * against our FastAPI backend. Falls back to a disabled note if PayPal is
 * not configured on the server yet.
 *
 * onApproved(sessionId): callback after a successful capture (main use case:
 * redirect the buyer to /payment/success?session_id=<sessionId>).
 */
export const PayPalCheckout = ({
  items,
  gift,
  onApproved,
  disabled,
  originUrl,
}) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    fetchPaypalConfig()
      .then((c) => { if (ok) setCfg(c); })
      .catch(() => ok && setCfg({ configured: false }))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, []);

  if (loading) {
    return (
      <p className="text-center text-[11px] text-ink/40 font-body pt-2"
         data-testid="paypal-loading">Loading PayPal…</p>
    );
  }

  if (!cfg?.configured || !cfg?.client_id) {
    return (
      <p className="text-center text-[11px] text-ink/40 font-body pt-2"
         data-testid="paypal-unavailable">
        PayPal is being configured — please use card checkout for now.
      </p>
    );
  }

  return (
    <div className={disabled ? "opacity-60 pointer-events-none" : ""}
         data-testid="paypal-wrapper">
      <PayPalScriptProvider
        options={{
          clientId: cfg.client_id,
          currency: "USD",
          intent: "capture",
          components: "buttons",
        }}
      >
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 44 }}
          disabled={disabled}
          forceReRender={[items?.length || 0, JSON.stringify(gift || {})]}
          createOrder={async () => {
            try {
              const { order_id } = await createPaypalOrder(
                items,
                originUrl || window.location.origin,
                gift || {},
              );
              return order_id;
            } catch (e) {
              toast.error("Unable to start PayPal checkout. Please try again.");
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await capturePaypalOrder(data.orderID);
              if (res.status === "completed" || res.status === "already_captured") {
                onApproved?.(data.orderID);
              } else {
                toast.error("PayPal payment is still processing — check your email for confirmation.");
              }
            } catch {
              toast.error("PayPal capture failed. Please try again.");
            }
          }}
          onError={() => toast.error("PayPal encountered an error. Please try card checkout.")}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
